import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { clsx } from "clsx";

import { jarvisApi, type JarvisRef } from "../api/jarvis";
import { draftKey, useDraft } from "../lib/hooks/useDraft";

/**
 * Fire-and-forget composer — drops a context-rich message into the operator's
 * own Jarvis session. No reply thread in the UI; Jarvis handles it inside her
 * session (may post back via Slack).
 *
 * Draft is persisted per ``(page, ref)`` via the shared ``useDraft`` hook so an
 * accidental navigation doesn't lose a long message.
 */
function composerKey(ref?: JarvisRef | null): string {
  if (!ref) return draftKey("jarvis", "freeform");
  return draftKey("jarvis", ref.type, ref.id);
}

/**
 * Carry drafts written under the pre-`useDraft` key scheme (`jarvis-draft:*`,
 * a bare string) into the shared `dux-draft:` namespace. Runs once at import,
 * before any composer mounts — someone may have a half-written message open
 * across the deploy, and losing it is the exact failure this hook exists to
 * prevent.
 */
(function migrateLegacyDrafts() {
  const LEGACY = "jarvis-draft:";
  try {
    const found: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(LEGACY)) found.push(key);
    }
    for (const key of found) {
      const text = window.localStorage.getItem(key);
      window.localStorage.removeItem(key);
      if (!text) continue;
      const next = draftKey("jarvis", key.slice(LEGACY.length));
      if (window.localStorage.getItem(next)) continue; // Newer draft wins.
      window.localStorage.setItem(
        next,
        JSON.stringify({ text, savedAt: Date.now() }),
      );
    }
  } catch {
    // Storage unavailable — nothing to migrate.
  }
})();

export function JarvisComposer({
  refObj,
  placeholder,
}: {
  refObj?: JarvisRef;
  placeholder?: string;
}) {
  const {
    draft: text,
    setDraft: setText,
    clearDraft,
  } = useDraft(composerKey(refObj));
  const [justSent, setJustSent] = useState(false);
  const [failedAt, setFailedAt] = useState<number | null>(null);

  // Clear transient "delivered" pill after a few seconds.
  useEffect(() => {
    if (!justSent) return;
    const id = window.setTimeout(() => setJustSent(false), 3000);
    return () => window.clearTimeout(id);
  }, [justSent]);

  useEffect(() => {
    if (failedAt === null) return;
    const id = window.setTimeout(() => setFailedAt(null), 5000);
    return () => window.clearTimeout(id);
  }, [failedAt]);

  const mutation = useMutation({
    mutationFn: () => jarvisApi.message(text.trim(), refObj),
    onSuccess: () => {
      clearDraft();
      setJustSent(true);
    },
    onError: () => setFailedAt(Date.now()),
  });

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (text.trim() && !mutation.isPending) mutation.mutate();
    } else if (e.key === "Escape") {
      (e.target as HTMLTextAreaElement).blur();
    }
  };

  const canSend = text.trim().length > 0 && !mutation.isPending;

  return (
    <div className="panel">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="label" style={{ marginBottom: 0 }}>
          Message Jarvis
        </span>
        <span className="jarvis-presence" title="Fire-and-forget — Jarvis handles it in her session">
          <span className="dot" />
          fire &amp; forget
        </span>
      </div>
      <textarea
        className="input"
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKey}
        placeholder={
          placeholder ??
          (refObj?.type === "campaign"
            ? "Tell Jarvis something about this campaign — she'll see it in her session."
            : refObj?.type === "pipeline"
              ? "Tell Jarvis something about this pipeline — she'll see it in her session."
              : "Tell Jarvis something about this contact — she'll see it in her session.")
        }
        style={{ resize: "vertical", minHeight: 72 }}
      />
      <div className="flex items-center justify-between gap-2 mt-2">
        <span className="text-xs text-ink-low">⌘↵ to send</span>
        <button
          type="button"
          className={clsx("btn btn-second btn-sm")}
          disabled={!canSend}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? "sending…" : "Send to Jarvis"}
        </button>
      </div>
      {justSent ? (
        <div className="mt-2">
          <span className="pill pill--ok">delivered</span>
        </div>
      ) : null}
      {failedAt ? (
        <div className="mt-2">
          <span className="pill pill--signal">Send failed — try again</span>
        </div>
      ) : null}
    </div>
  );
}
